import { Observable } from 'rxjs';
import { IResponse } from '../global.interface';

export enum CommentOrderByStrategy {
    Recent = 1,
    MostUpvoted = 2,
    MostAnswered = 3,
    Unanswered = 4,
}

export enum CommentVoteState {
    downvoted = -1,
    none = 0,
    upvoted = 1,
}

export enum QuizCommentType {
    descriptionComments = 1,
    questionComments = 3,
}

export interface ICommentsApiConfig {
    environmentUrl: string;
    useMock?: boolean;
    mock?: ICommentsApiMock;
}

export interface ICommentsApiMock {
    quizComments: IQuizCommentResponse;
    quizCommentsCount: IQuizCommentsCountResponse;
    quizCommentsVote: IQuizCommentsVoteResponse;
    ccCommentsResponse: ICCCommentsResponse;
}

export interface ICommentsApi {
    getQuizComments: (
        quizId: number,
        query: IQuizCommentQuery
    ) => Observable<IQuizCommentResponse>;
    getCCComments: (query: ICCCommentQuery) => Observable<ICCCommentsResponse>;
    getLPComments: (
        commentContainerId: number,
        query: ICommentQueryBase
    ) => Observable<ILPComment[]>;
    writeLPComment: (
        commentContainerId: number,
        payload: ILPCommentBase
    ) => Observable<ILPComment>;
    editLPComment: (
        commentContainerId: number,
        commentId: number,
        payload: ILPCommentBase
    ) => Observable<ILPComment>;
    deleteLPComment: (
        commentContainerId: number,
        commentId: number
    ) => Observable<null>;
    voteLPComment: (
        commentContainerId: number,
        commentId: number,
        payload: ILPCommentVotePayload
    ) => Observable<CommentVoteState>;
    getLPCommentsCount: (commentContainerId: number) => Observable<number>;
    getCCCommentsCount: (ccId: number) => Observable<ICCCommentsCountResponse>;
    writeQuizComment: (
        quizId: number,
        payload: IQuizCommentBase
    ) => Observable<IQuizCommentResponse>;
    writeCCComment: (
        payload: ICCCommentBase
    ) => Observable<ICCCommentsResponse>;
    deleteQuizComment: (
        quizId: number,
        commentId: number
    ) => Observable<IQuizCommentResponse>;
    deleteCCComment: (
        payload: IDeleteCCComment
    ) => Observable<ICCCommentsResponse>;
    editQuizComment: (
        quizId: number,
        commentId: number,
        message: string
    ) => Observable<IQuizCommentResponse>;
    editCCComment: (
        payload: IEditCCCommentPayload
    ) => Observable<ICCCommentsResponse>;
    voteQuizComment: (
        quizId: number,
        commentId: number,
        vote: CommentVoteState
    ) => Observable<IQuizCommentsVoteResponse>;
    voteCCComment: (
        payload: ICCCommentVotePayload
    ) => Observable<ICCCommentsVoteResponse>;
    getQuizCommentsCount: (
        quizId: number,
        query: { type: QuizCommentType }
    ) => Observable<IQuizCommentsCountResponse>;
    getLessonsCommentsCounts: (
        courseId: number
    ) => Observable<ILessonsCommentsCountsResponse>;
}

export interface ICommentQueryBase {
    count: number;
    index: number;
    parentId: number;
    orderby: CommentOrderByStrategy;
}

export interface IQuizCommentQuery extends ICommentQueryBase {
    type: QuizCommentType;
}

export interface ICCCommentQuery extends ICommentQueryBase {
    problemId: number;
}

export interface ICCCommentVotePayload {
    problemId: number;
    id: number;
    vote: CommentVoteState;
}

export interface ILPCommentVotePayload {
    vote: CommentVoteState;
}

export interface IQuizCommentBase {
    message: string;
    type: QuizCommentType;
    parentId?: number;
    children?: IQuizComment[];
}

export interface ICCCommentBase {
    message: string;
    problemId: number;
    date?: string;
    parentId?: number;
    children?: ICCComment[];
}

export interface ILPCommentBase {
    message: string;
    date?: string;
    parentId?: number;
    relationId?: number;
    children?: ILPComment[];
}

export interface IDeleteCCComment {
    id: number;
    parentId?: number;
}

export interface ICommentResponseBaseQuiz {
    id: number;
    userId: number;
    date: string;
    replies: number;
    vote: CommentVoteState;
    votes: number;
    index: number;
    userName: string;
    avatarUrl: string;
    hasAvatar: boolean;
    level: number;
    xp: number;
    accessLevel: number;
    badge: string;
}

export interface IQuizComment
    extends IQuizCommentBase,
        ICommentResponseBaseQuiz {
    quizId: number;
}

export interface ICCComment {
    problemId: number;
    parentID?: number;
    id: number;
    userID: number;
    date: string;
    replies: number;
    vote: CommentVoteState;
    votes: number;
    index: number;
    userName: string;
    avatarUrl: string;
    hasAvatar: boolean;
    level: number;
    xp: number;
    accessLevel: number;
    badge: string;
    message: string;
    status: number;
}

export interface ILPComment {
    id: number;
    parentID: number;
    userID: number;
    materialID: number;
    message: string;
    date: string;
    votes: number;
    userName?: string;
    avatarUrl?: string;
    hasAvatar?: boolean;
    level?: number;
    xp?: number;
    accessLevel?: number;
    badge?: string;
    vote?: CommentVoteState;
    replies?: number;
    index?: number;
}

export interface IQuizCommentResponse extends IResponse {
    data: IQuizComment | IQuizComment[];
}

export interface ICCCommentsResponse extends IResponse {
    data: ICCComment | ICCComment[];
}

export interface IQuizCommentsCountResponse extends IResponse {
    data: number;
}

export interface ICCCommentsCountResponse extends IResponse {
    data: number;
}

export interface IEditCCCommentPayload {
    problemId: number;
    message: string;
    id: number;
    date?: string;
    parentID?: number;
}

export interface IQuizCommentsVoteResponse extends IResponse {
    data: CommentVoteState;
}

export interface ICCCommentsVoteResponse extends IResponse {
    data: CommentVoteState;
}

export interface ILessonCommentsCount {
    lessonId: number;
    commentsCount: number;
}

export interface ILessonsCommentsCountsResponse extends IResponse {
    data: ILessonCommentsCount[];
}
